import { cn } from "@/lib/utils";
import { formatMoney, formatPips } from "@/lib/format";
import { useCurrency } from "@/hooks/useCurrency";

/**
 * Null renders "—", never 0 — see lib/format.ts's header comment for why that matters here.
 *
 * M26a — the currency defaults to the account's own (via `CurrencyProvider` in the shell),
 * so bare `<MoneyDelta value={...} />` renders EUR on a EUR account instead of the "$" it
 * hardcoded before. The explicit prop remains as an override for a figure that is genuinely
 * denominated in something else.
 */
export function MoneyDelta({ value, currency }: { value: string | number | null | undefined; currency?: string }) {
  const accountCurrency = useCurrency();
  const n = value === null || value === undefined ? null : Number(value);
  const positive = n !== null && n > 0;
  const negative = n !== null && n < 0;
  return (
    <span className={cn("tabular-nums font-medium", positive && "text-success", negative && "text-destructive")}>
      {formatMoney(value, currency ?? accountCurrency)}
    </span>
  );
}

export function PipsDelta({ value }: { value: string | number | null | undefined }) {
  const n = value === null || value === undefined ? null : Number(value);
  const positive = n !== null && n > 0;
  const negative = n !== null && n < 0;
  return (
    <span className={cn("tabular-nums", positive && "text-success", negative && "text-destructive")}>
      {formatPips(value)}
    </span>
  );
}

/**
 * M40a — what a position has cost to hold and to open, beside its P/L.
 *
 * Swap is negligible on a day trade and not at all on one left running for a week, and
 * until now it was visible only inside the history window's total. Shown on every position
 * and every closed order, so the number a card reports as "profit" can be read against what
 * it cost. Renders nothing while neither figure is known — an open order that has not filled,
 * or a closed one whose deals are not yet recorded — rather than a row of dashes.
 */
export function CostsLine({
  swap,
  commission,
}: {
  swap: string | number | null | undefined;
  commission: string | number | null | undefined;
}) {
  const accountCurrency = useCurrency();
  if ((swap === null || swap === undefined) && (commission === null || commission === undefined)) return null;
  return (
    <span className="text-xs tabular-nums text-muted-foreground">
      swap {formatMoney(swap, accountCurrency)} · commission {formatMoney(commission, accountCurrency)}
    </span>
  );
}
